import "mapbox-gl/dist/mapbox-gl.css";
import React, { useState, useRef, useEffect } from "react";
import MapGL, { MapRef, Source, Layer, Popup, Marker } from "react-map-gl";
import { Box } from "@material-ui/core";
import { GridFilterModel } from "@mui/x-data-grid-pro";

import httpClient from "../../lib/httpClient";
import { apiError } from "../../lib/apiError";
import Pin from "../map/pin";
import { lightMapStye } from "../map/Constants";
import { filterModelToQueryValue, getRandomInt } from "../util/tableUtils";
import { MiningAssetsLegend } from "../legends/MiningAssetsLegend";
import { MapStylesMenu } from "../map/MapStylesMenu";
import { ScaleBar } from "../map/ScaleBar";
import { RenderBackgroundLayers } from "./RenderBackgroundLayers";
import { emptyGeojson } from "../riskAssessment/defaultSourceLayer";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

type MiningAssetsMapProps = {
  miningAssets: MiningAsset[];
  filterModel: GridFilterModel;
  activeLayers: ActiveLayers;
};

export const MiningAssetsMap: React.FC<MiningAssetsMapProps> = ({
  miningAssets: markers,
  filterModel,
  activeLayers,
}) => {
  const mapRef = useRef<MapRef>(null);

  const [viewState, setViewState] = useState<PartialViewState>({
    latitude: 27.300804,
    longitude: 11.866934,
    zoom: 1.6,
  });

  const [mapStyle, setMapStyle] = useState(lightMapStye);
  const [geoJson, setGeoJson] = useState<GeoJsonData>(emptyGeojson);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [selectedMiningAsset, setSelectedMiningAsset] = useState<MiningAsset | null>(null);

  useEffect(() => {
    const getGeojson = async () => {
      try {
        const updatedGeojson = await httpClient().get(
          `/miningAssets/geojson?filters=${filterModelToQueryValue(filterModel)}`,
        );
        setGeoJson(updatedGeojson.data);
      } catch (error) {
        apiError(error);
      }
    };
    getGeojson();
  }, [filterModel]);

  const onMove = (viewState: PartialViewState) => {
    setViewState(viewState);
  };

  const onPinClick = (markerMiningAsset: MiningAsset) => {
    setLongitude(markerMiningAsset.longitude);
    setLatitude(markerMiningAsset.latitude);
    setSelectedMiningAsset({ ...markerMiningAsset });
  };

  return (
    <MapGL
      {...viewState}
      mapboxAccessToken={TOKEN}
      style={{ width: "100%", height: "720px" }}
      onMove={(evt) => onMove(evt.viewState)}
      mapStyle={mapStyle}
      ref={mapRef}
    >
      <RenderBackgroundLayers activeLayers={activeLayers} />

      {markers &&
        markers.map((marker) => (
          <Marker
            key={`${marker.name}${marker.type}${getRandomInt()}`}
            longitude={marker.longitude}
            latitude={marker.latitude}
          >
            <Pin size={20} onClick={() => onPinClick(marker)} />
          </Marker>
        ))}

      {selectedMiningAsset && (
        <Popup longitude={longitude} latitude={latitude} onClose={() => setSelectedMiningAsset(null)}>
          <Box m={1}>
            <div>
              <b>Name</b>: {selectedMiningAsset.name}
            </div>
            <div>
              <b>Country</b>: {selectedMiningAsset.country}
            </div>
            <div>
              <b>Type</b>: {selectedMiningAsset.type}
            </div>
            <div>
              <b>Owner</b>: {selectedMiningAsset.owner}
            </div>
          </Box>
        </Popup>
      )}

      {activeLayers.assetsLayerIsActive && (
        <Source id="mining-assets" type="geojson" data={geoJson}>
          <Layer
            id="mining-assets-layer"
            type="circle"
            source="mining-assets"
            paint={{
              "circle-radius": 6,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
              "circle-color": [
                "match",
                ["get", "type"],
                "coalPlant",
                "#F6924E", // Orange
                "coalMine",
                "#8FCCA7", // Green
                "coalTerminal",
                "#2A81BC", // Blue
                "ironOreMine",
                "#FEE196", // Yellow
                "#9b59b6", // Default (Purple)
              ],
            }}
          />
        </Source>
      )}

      <MapStylesMenu setMapStyle={setMapStyle}></MapStylesMenu>

      <MiningAssetsLegend activeLayers={activeLayers} />
      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};
